import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { WarningIcon } from "./assets/warning-icon";
import { formatDateToYYYYMMDD } from "@/utils/date-formatter";
import { BellImage } from "./assets/bell-image";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

const LoadingSkeletonHeader: React.FC = () => (
  <div className="flex items-center justify-between mb-4 pb-4 border-b border-[rgba(233,234,235,1)]">
    <div className="flex items-center gap-2">
      <Skeleton className="w-4 h-4" />
      <Skeleton className="w-20 h-4" />
      <Skeleton className="w-16 h-3" />
    </div>
  </div>
);

export const LoadingSkeletonSingleCard: React.FC = () => (
  <div className="p-6 max-w-[520px] w-full h-auto bg-white rounded-[20px]">
    <LoadingSkeletonHeader />
    <div className="mt-2 flex flex-col gap-3">
      <Skeleton className="w-full aspect-[16/9] rounded-[16px]" />
      <Skeleton className="w-full h-5 rounded-[8px]" />
    </div>
  </div>
);

export const LoadingSkeletonMultiList: React.FC = () => (
  <div className="p-6 max-w-[520px] w-full h-auto bg-white rounded-[20px]">
    <LoadingSkeletonHeader />
    <div className="mt-2 space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="w-18 h-18 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const EmptyState: React.FC<{
  className?: string;
}> = ({
   className,
 }) => {
  const { t } = useTranslation();
  return (
    <div className={cn("mt-20 p-3 max-w-[620px] w-full rounded-[12px] bg-white", className)}>
      <span className="text-sm text-[rgba(16,24,40,1)] font-normal">
        {t('agent_creation.hi')}
      </span>
    </div>
  )
};

export const ExceptionState: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="p-3 max-w-[620px] w-full rounded-[12px] bg-white flex items-center">
      <WarningIcon className="size-5 mr-2" />
      <span className="text-sm text-[rgba(217,45,32,1)] font-medium">
        {t('agent_creation.server_error')} <a href="mailto:support@bizdesk.ai" target="_blank" rel="noopener noreferrer" className="text-[rgba(217,45,32,1)] underline">{t('agent_creation.contact_admin')}</a>
      </span>
    </div>
  )
};

export const ExpiredState: React.FC<{ expiredAt?: string }> = ({ expiredAt }) => {
  const { t } = useTranslation();
  return (
    <div className="p-3 max-w-[620px] w-full rounded-[12px] bg-white flex items-center">
      <BellImage className="size-4 mr-2" />
      <span className="text-[15px] text-[rgba(16,24,40,1)] font-medium">
        {t('agent_creation.expired_tips', { expiredAt: formatDateToYYYYMMDD(expiredAt) })} <a href="mailto:support@bizdesk.ai" target="_blank" rel="noopener noreferrer" className=" underline">{t('agent_creation.contact_service')}</a>
      </span>
    </div>
  )
};